import type { QueryClient } from '@tanstack/react-query';
import type { ChatbotStopRequest } from '@/models/chatbot';
import useChatbotStore from '../store/useChatbotStore';
import { chatbotQueryKeys } from './chatbotQueryKeys';
import type { StreamCallbacks } from './useChatbotStream';

interface StopChatbotOptions {
  requestRef: { current: AbortController | null };
  abortStream: VoidFunction;
  stopStream: (request: ChatbotStopRequest) => Promise<void>;
  onError: VoidFunction;
}

const getStoreActions = () => useChatbotStore.getState();

export const waitForChatbotRequest = <Result>(operation: Promise<Result>, signal: AbortSignal): Promise<Result> =>
  new Promise((resolve, reject) => {
    const handleAbort = (): void => reject(signal.reason);
    signal.addEventListener('abort', handleAbort, { once: true });
    if (signal.aborted) handleAbort();
    operation.then(
      result => {
        signal.removeEventListener('abort', handleAbort);
        resolve(result);
      },
      error => {
        signal.removeEventListener('abort', handleAbort);
        reject(error);
      },
    );
  });

export const getStreamCallbacks = (
  sessionId: string,
  signal: AbortSignal,
  queryClient: QueryClient,
): StreamCallbacks => {
  const guardCallback =
    <Arguments extends unknown[]>(callback: (...parameters: Arguments) => void) =>
    (...parameters: Arguments): void => {
      if (!signal.aborted && getStoreActions().activeSessionId === sessionId) {
        callback(...parameters);
      }
    };

  return {
    onTextChunk: guardCallback(text => getStoreActions().appendStreamingContent(text)),
    onFileEvent: guardCallback(file => getStoreActions().addStreamingFile(file)),
    onReasoningChunk: guardCallback(text => getStoreActions().addStreamingReasoningStep(text)),
    onError: guardCallback(error => getStoreActions().setError({ message: error, retryable: true })),
    onDone: guardCallback(() => {
      getStoreActions().finalizeAiMessage();
      queryClient.invalidateQueries({ queryKey: chatbotQueryKeys.conversations });
    }),
    onRateLimit: guardCallback(rateLimit => getStoreActions().updateRateLimit(rateLimit)),
  };
};

export const stopChatbotRequest = async ({
  requestRef,
  abortStream,
  stopStream,
  onError,
}: StopChatbotOptions): Promise<void> => {
  const store = getStoreActions();
  const { isStreaming: streaming, isStopping, activeConversationId: stoppingConversationId, activeSessionId } = store;

  if (!streaming || isStopping) return;

  const stoppingRequest = requestRef.current;
  store.setStopping(true);
  stoppingRequest?.abort();
  abortStream();
  store.finalizeAiMessage({ interrupted: true });
  store.setStreaming(false);

  try {
    if (stoppingConversationId) {
      await stopStream({
        conversation_id: stoppingConversationId,
        ...(activeSessionId ? { session_id: activeSessionId } : {}),
      });
    }
  } catch {
    onError();
  } finally {
    if (!requestRef.current || requestRef.current === stoppingRequest) {
      store.setStopping(false);
    }
    if (getStoreActions().activeSessionId === activeSessionId) {
      store.clearActiveSessionId();
    }
  }
};
